import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { ONBOARDING_STEPS, GOAL_TYPES, TIMEFRAMES } from "../constants/onboardingData";
import { api, formatApiError } from "../lib/api";
import { NovaOrb } from "../components/NovaOrb";

export default function Onboarding() {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({
    injuries: [],
    red_flags: [],
    goal: { type: null, target_value: "", timeframe_months: null },
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const step = ONBOARDING_STEPS[stepIdx];
  const isLast = stepIdx === ONBOARDING_STEPS.length - 1;

  const goNext = () => {
    if (isLast) handleSubmit();
    else setStepIdx((i) => i + 1);
  };

  const selectSingle = (value) => {
    const updated = { ...answers, [step.key]: value };
    setAnswers(updated);
    if (isLast) {
      handleSubmit(updated);
    } else {
      setTimeout(() => setStepIdx((i) => Math.min(i + 1, ONBOARDING_STEPS.length - 1)), 260);
    }
  };

  const toggleMulti = (value) => {
    setAnswers((a) => {
      const current = new Set(a[step.key] || []);
      if (value === "none") return { ...a, [step.key]: current.has("none") ? [] : ["none"] };
      current.delete("none");
      if (current.has(value)) current.delete(value);
      else current.add(value);
      return { ...a, [step.key]: Array.from(current) };
    });
  };

  const setGoalField = (field, value) => {
    setAnswers((a) => ({ ...a, goal: { ...a.goal, [field]: value } }));
  };

  const handleSubmit = async (overrideAnswers) => {
    const a = overrideAnswers || answers;
    setSubmitting(true);
    setError("");
    const goalTypeMeta = GOAL_TYPES.find((g) => g.value === a.goal.type);
    const payload = {
      goal: {
        type: a.goal.type,
        target_value: goalTypeMeta?.hasTarget && a.goal.target_value ? Number(a.goal.target_value) : null,
        target_unit: goalTypeMeta?.hasTarget ? goalTypeMeta.unit : null,
        timeframe_months: Number(a.goal.timeframe_months),
      },
      experience: a.experience,
      injuries: (a.injuries || []).filter((v) => v !== "none"),
      days_per_week: Number(a.days_per_week),
      session_minutes: Number(a.session_minutes),
      equipment_tier: a.equipment_tier,
      preferred_time: a.preferred_time,
      diet_pref: a.diet_pref || "veg",
      red_flags: (a.red_flags || []).filter((v) => v !== "none"),
      age: Number(a.age),
      gender: a.gender || null,
    };
    try {
      await api.post("/onboarding/submit", payload);
      setSubmitting(false);
      navigate("/app/plan/preview");
    } catch (e) {
      setSubmitting(false);
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const isNumber = step.type === "number";
  const isMulti = step.type === "multi";
  const isGoal = step.type === "goal";
  const goalTypeMeta = GOAL_TYPES.find((g) => g.value === answers.goal.type);
  const goalCanContinue =
    !!answers.goal.type &&
    !!answers.goal.timeframe_months &&
    (!goalTypeMeta?.hasTarget || !!answers.goal.target_value);
  const canContinue = isNumber ? !!answers[step.key] : isGoal ? goalCanContinue : true;

  return (
    <div className="min-h-screen bg-kaizen-bg flex flex-col px-6 py-8 relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_50%_0%,rgba(139,93,199,0.14),transparent_60%)]" />

      <AnimatePresence>
        {submitting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-kaizen-bg flex flex-col items-center justify-center px-8"
            data-testid="plan-building-overlay"
          >
            <NovaOrb size={100} />
            <p className="font-display italic text-xl text-kaizen-text mt-6 text-center" data-testid="plan-building-title">
              Building your week…
            </p>
            <p className="text-sm text-kaizen-dim mt-2 text-center max-w-xs">
              Nova's mapping this to your goals, your equipment, and anything you told her to watch out for.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-md w-full mx-auto flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-10" data-testid="onboarding-progress">
          {ONBOARDING_STEPS.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${i <= stepIdx ? "bg-kaizen-violet" : "bg-white/[0.08]"}`} />
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step.key}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.35 }}
            className="flex-1"
          >
            <h2 className="font-display text-2xl sm:text-3xl text-kaizen-text leading-snug" data-testid="onboarding-question">
              {step.question}
            </h2>
            {step.note && <p className="mt-2 text-xs text-kaizen-dim2">{step.note}</p>}

            {isGoal ? (
              <div className="mt-8 space-y-2.5">
                {GOAL_TYPES.map((g) => (
                  <button
                    key={g.value}
                    onClick={() => setGoalField("type", g.value)}
                    data-testid={`onboarding-goal-type-${g.value}`}
                    className={`w-full flex items-center justify-between text-left px-5 py-3.5 rounded-2xl border transition-colors duration-300 ${
                      answers.goal.type === g.value
                        ? "border-kaizen-violet bg-kaizen-violet/12 text-kaizen-violetSoft"
                        : "border-white/[0.08] bg-white/[0.02] text-kaizen-text/90 hover:border-white/20"
                    }`}
                  >
                    <span className="text-sm">{g.label}</span>
                    {answers.goal.type === g.value && <Check size={16} />}
                  </button>
                ))}

                {goalTypeMeta?.hasTarget && (
                  <div className="pt-2">
                    <label className="text-xs text-kaizen-dim">Target ({goalTypeMeta.unit})</label>
                    <input
                      type="number"
                      value={answers.goal.target_value}
                      onChange={(e) => setGoalField("target_value", e.target.value)}
                      data-testid="onboarding-goal-target-input"
                      className="mt-1.5 w-full bg-white/[0.04] border border-white/[0.09] rounded-xl px-4 py-2.5 text-sm text-kaizen-text outline-none focus:border-kaizen-violet/60 transition-colors duration-300"
                    />
                  </div>
                )}

                <div className="pt-2">
                  <label className="text-xs text-kaizen-dim">Timeframe</label>
                  <div className="mt-1.5 flex gap-2">
                    {TIMEFRAMES.map((t) => (
                      <button
                        key={t.value}
                        onClick={() => setGoalField("timeframe_months", t.value)}
                        data-testid={`onboarding-goal-timeframe-${t.value}`}
                        className={`flex-1 py-2.5 rounded-xl text-sm border transition-colors duration-300 ${
                          answers.goal.timeframe_months === t.value
                            ? "border-kaizen-violet bg-kaizen-violet/12 text-kaizen-violetSoft"
                            : "border-white/[0.08] text-kaizen-dim"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : isNumber ? (
              <input
                type="number"
                autoFocus
                value={answers[step.key] || ""}
                onChange={(e) => setAnswers((a) => ({ ...a, [step.key]: e.target.value }))}
                data-testid="onboarding-number-input"
                className="mt-8 w-full bg-white/[0.04] border border-white/[0.09] rounded-xl px-4 py-3 text-lg text-kaizen-text outline-none focus:border-kaizen-violet/60 transition-colors duration-300"
              />
            ) : (
              <div className="mt-8 space-y-2.5">
                {step.options.map((opt) => {
                  const selected = isMulti ? (answers[step.key] || []).includes(opt.value) : answers[step.key] === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => (isMulti ? toggleMulti(opt.value) : selectSingle(opt.value))}
                      data-testid={`onboarding-option-${step.key}-${opt.value}`}
                      className={`w-full flex items-center justify-between text-left px-5 py-3.5 rounded-2xl border transition-colors duration-300 ${
                        selected
                          ? "border-kaizen-violet bg-kaizen-violet/12 text-kaizen-violetSoft"
                          : "border-white/[0.08] bg-white/[0.02] text-kaizen-text/90 hover:border-white/20"
                      }`}
                    >
                      <span className="text-sm">{opt.label}</span>
                      {selected && <Check size={16} />}
                    </button>
                  );
                })}
                {step.noneOption && (
                  <button
                    onClick={() => toggleMulti("none")}
                    data-testid={`onboarding-option-${step.key}-none`}
                    className={`w-full flex items-center justify-between text-left px-5 py-3.5 rounded-2xl border transition-colors duration-300 ${
                      (answers[step.key] || []).includes("none")
                        ? "border-kaizen-violet bg-kaizen-violet/12 text-kaizen-violetSoft"
                        : "border-white/[0.08] bg-white/[0.02] text-kaizen-text/90 hover:border-white/20"
                    }`}
                  >
                    <span className="text-sm">None of these</span>
                    {(answers[step.key] || []).includes("none") && <Check size={16} />}
                  </button>
                )}
                {step.optional && (
                  <button
                    onClick={() => selectSingle(null)}
                    data-testid="onboarding-skip-button"
                    className="w-full text-center py-2 text-xs text-kaizen-dim2 hover:text-kaizen-dim transition-colors duration-300"
                  >
                    Prefer to skip
                  </button>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {error && <p className="mt-4 text-xs text-ready-rose" data-testid="onboarding-error">{error}</p>}
        {(isMulti || isNumber || isGoal) && (
          <button
            onClick={goNext}
            disabled={!canContinue || submitting}
            data-testid="onboarding-continue-button"
            className="glow-cta mt-8 w-full py-3.5 rounded-full bg-kaizen-violet text-[#140A24] font-semibold text-sm disabled:opacity-50 hover:scale-[1.01] active:scale-[0.98] transition-transform duration-300"
          >
            {submitting ? "Building your plan…" : isLast ? "See my plan" : "Continue"}
          </button>
        )}
      </div>
    </div>
  );
}
